/**
 * EditEventPage — the event form, at `/event/events/:eventId/edit`.
 *
 * Route: `/event/events/:eventId/edit`. Reached from the detail page's "Edit Event", and
 * directly from the create dialog when the marketer chose "Fill in Details Now".
 *
 * A page rather than a dialog because it carries most of the event's writable fields.
 * Follows the Figma's "Fill in Event Details" (frames 241:11253 and 243:15264).
 *
 * Configure Nomination Form is here, but only the configurable half of it. The nomination
 * form a BD fills in has a fixed Contact Information section and a Travel & Event
 * Arrangements section made entirely of questions this event defines — so this page
 * configures the latter and merely lists the former. Filling the form in is a separate page
 * (CreateNominationPage). See NominationFormConfig.
 *
 * Configure Event Hierarchy IS here, as a tree rather than the design's two fixed fields —
 * see CampaignHierarchy. It is not part of `UpdateEventInput`: the service keeps it as
 * campaign-node resources, loaded with the event and saved after the patch (hierarchySync).
 *
 * Event Introduction is the event's `description`, as rich text with images (RichTextEditor). An
 * image is uploaded the moment it is inserted (`uploadEventFile`) and saved as its id; see
 * utils/eventDescription for the format both sides keep. Emptying it clears it on save.
 *
 * Nomination Required, the nomination window and Configure Nomination Form sit together in
 * one Nomination section (testing feedback: they were spread over the page). The window and
 * the form appear only when Nomination Required is Yes, which is also when the service
 * starts caring about the window: it refuses to publish a nomination-enabled event
 * without both bounds, and the BD landing page's "Open for Nomination" count is derived
 * from them.
 *
 * Saving is `UpdateEvent` and nothing else — a PATCH, see {@link buildPatch} for what that
 * means for a cleared field. It does NOT publish: an event stays a DRAFT until someone
 * publishes it from the detail page, which is where the design puts that button.
 *
 * (This briefly did publish on save, on the theory that a saved event is a published one.
 * The design says otherwise: editing and publishing are separate decisions, and a marketer
 * filling in details over several sittings should not go live on the first save.)
 *
 * One save button rather than a Save-as-Draft / Confirm pair, though, because the service
 * owns `contentStatus` and will not take it here — both would issue the identical request.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autosuggest,
  BreadcrumbGroup,
  Button,
  ColumnLayout,
  Container,
  ContentLayout,
  DatePicker,
  Form,
  FormField,
  Header,
  Input,
  Multiselect,
  RadioGroup,
  Select,
  SpaceBetween,
  type MultiselectProps,
  type SelectProps,
} from '@amzn/awsui-components-react';
import { BUSINESS_UNITS } from '../../businessUnits';
import { fieldsToClear, listForSave, type ClearableKey } from '../../utils/clearFields';
import {
  effectiveTargetMode,
  isAllBusinessUnits,
  targetBusinessUnits,
  targetsForSave,
  type TargetMode,
} from '../../utils/buTargets';
import {
  PROVINCES,
  cityAfterProvinceChange,
  citiesOf,
  initialRegion,
} from '../../utils/cnRegions';
import {
  CampaignHierarchy,
  type EventPickerSource,
  type HierarchyNode,
} from '../../components/CampaignHierarchy';
import {
  NominationFormConfig,
  questionsForSave,
  questionsFrom,
  type NominationQuestion,
} from '../../components/NominationFormConfig';
import {
  eventHubCalendarClient,
  type Event,
  type EventLifecycleStage,
  type EventTargets,
  type EventSubType,
  type EventType,
  type UpdateEventInput,
} from '../../client/EventCalendarClient';
import {
  LIFECYCLE_STAGES,
  SUB_TYPE_LABELS,
  TYPE_META,
  lifecycleStageToSend,
} from '../../utils/eventTaxonomy';
import { fromDateInput, toDateInput } from '../../utils/eventDates';
import { canManageEvents } from '../../utils/authorization';
import { descriptionForSave } from '../../utils/eventDescription';
import { RichTextEditor } from '../../components/RichTextEditor';
import { useEventHubRoles } from '../../hooks/useEventHubRoles';
import './EditEventPage.css';
import { EventTypeIcon } from '../../components/EventTypeIcon';
import { useEventHubLoading } from '../../components/EventHubLayout/EventHubLoading';
import {
  EMPTY_HIERARCHY,
  eventIdsWithNodes,
  loadHierarchy,
  saveHierarchy,
  type LoadedHierarchy,
} from '../../components/CampaignHierarchy/hierarchySync';

const TYPE_OPTIONS: SelectProps.Option[] = (
  Object.keys(TYPE_META) as EventType[]
).map((t) => ({ value: t, label: TYPE_META[t].label, iconSvg: <EventTypeIcon type={t} /> }));

const SUB_TYPE_OPTIONS: SelectProps.Option[] = (
  Object.keys(SUB_TYPE_LABELS) as EventSubType[]
).map((t) => ({ value: t, label: SUB_TYPE_LABELS[t] }));

const BU_OPTIONS: MultiselectProps.Option[] = BUSINESS_UNITS.map((bu) => ({
  value: bu,
  label: bu,
}));

const PROVINCE_OPTIONS = PROVINCES.map((p) => ({ value: p }));


/** Every target is three numbers, held as strings while being typed. */
interface TargetDraft {
  nomination: string;
  registration: string;
  attendance: string;
}

const EMPTY_TARGET: TargetDraft = { nomination: '', registration: '', attendance: '' };

/** The whole form, as strings — what the controls hand back. */
interface Draft {
  eventName: string;
  eventType: string;
  eventSubType: string;
  /** Province and city: the EC city list as suggestions, free text allowed — see cnRegions. */
  province: string;
  city: string;
  /** This event's OWN campaign id in SFDC — not the hierarchy beneath it. */
  sfdcCampaignId: string;
  startDate: string;
  endDate: string;
  businessUnits: string[];
  nominationRequired: 'no' | 'yes';
  nominationStartDate: string;
  nominationEndDate: string;
  targetMode: TargetMode;
  overall: TargetDraft;
  /** Per-BU targets, keyed by BU name. Only the ones actually filled in get sent. */
  byBu: Record<string, TargetDraft>;
  /** Whether the hierarchy editor is showing. */
  configureHierarchy: 'no' | 'yes';
  /** The campaign tree hanging off this event. */
  hierarchy: HierarchyNode[];
  /** Questions the nomination form asks for this event. */
  nominationQuestions: NominationQuestion[];
  /** The execution stage picked in the page header. Settable only once the event is published. */
  lifecycleStage: EventLifecycleStage | undefined;
  /** The introduction as the editor holds it (HTML; images carry a local or presigned `src`). */
  description: string;
}

function targetDraftOf(t: EventTargets | undefined): TargetDraft {
  return {
    nomination: t?.nominationTarget?.toString() ?? '',
    registration: t?.registrationTarget?.toString() ?? '',
    attendance: t?.attendanceTarget?.toString() ?? '',
  };
}

/**
 * Seeds the form from the event.
 *
 * The target mode follows the data rather than defaulting: an event that already has
 * per-BU targets opens on the By BU editor, so a marketer does not have to rediscover
 * where the numbers they entered last time went.
 */
function draftOf(event: Event): Draft {
  const byBu: Record<string, TargetDraft> = {};
  for (const t of event.byBusinessUnitTargets ?? []) {
    if (t.businessUnit) byBu[t.businessUnit] = targetDraftOf(t.targets);
  }
  return {
    eventName: event.eventName ?? '',
    eventType: event.eventType ?? '',
    eventSubType: event.eventSubType ?? '',
    ...initialRegion(event.province, event.city),
    sfdcCampaignId: event.sfdcCampaignId ?? '',
    startDate: toDateInput(event.startDate),
    endDate: toDateInput(event.endDate),
    businessUnits: [...(event.businessUnits ?? [])],
    nominationRequired: event.bdNominationSupported ? 'yes' : 'no',
    nominationStartDate: toDateInput(event.nominationStartDate),
    nominationEndDate: toDateInput(event.nominationEndDate),
    targetMode: Object.keys(byBu).length > 0 ? 'byBu' : 'overall',
    overall: targetDraftOf(event.overallTarget),
    byBu,
    /* Empty here: the hierarchy is a separate resource, loaded alongside the event (see the
       load effect), which then follows the data — 'yes' when there is a saved tree — so a saved
       hierarchy is never hidden behind the radio. */
    configureHierarchy: 'no',
    hierarchy: [],
    /* Follows the data, like targetMode above: `nominationForm` is the stored list, and a
       saved question invisible until the marketer happened to look would be worse than not
       having the editor. */
    nominationQuestions: questionsFrom(event.nominationForm),
    lifecycleStage: event.lifecycleStage,
    description: event.description ?? '',
  };
}

/** A typed number, or undefined when the box is empty or not a number. */
function num(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

function targetsOf(d: TargetDraft): EventTargets | undefined {
  const targets: EventTargets = {
    nominationTarget: num(d.nomination),
    registrationTarget: num(d.registration),
    attendanceTarget: num(d.attendance),
  };
  // All three empty means "no targets", not "targets of nothing".
  return Object.values(targets).some((v) => v !== undefined) ? targets : undefined;
}

/**
 * Turns the draft into an UpdateEvent input.
 *
 * `UpdateEvent` is a PATCH: an omitted field is left as it is. That means an empty box
 * sends `undefined` and therefore does NOT clear a value that is already stored — there
 * is no way to express "set this back to nothing" in the current model. The form shows
 * what was actually saved afterwards, so this is visible rather than silent, and it is
 * called out here because it reads as a bug the first time someone hits it.
 *
 * The nomination window is only sent when Nomination Required is Yes. Sending dates for
 * an event that does not take nominations would store bounds nothing consults.
 */
function buildPatch(
  eventId: string,
  d: Draft,
  formLocked: boolean,
  stored: Event | undefined,
): UpdateEventInput {
  const patch = buildFields(eventId, d, formLocked, stored);
  /* An emptied box has to be named to be cleared — see utils/clearFields. Only what the form is
     showing: the nomination window while nominations are on, the overall target while Overall is
     the mode in effect. */
  const nominationEnabled = d.nominationRequired === 'yes';
  const overallShown = effectiveTargetMode(d.targetMode, d.businessUnits) === 'overall';
  const edited: ClearableKey[] = [
    'sfdcCampaignId',
    'eventType',
    'eventSubType',
    'province',
    'city',
    'startDate',
    'endDate',
    ...(nominationEnabled ? (['nominationStartDate', 'nominationEndDate'] as const) : []),
    ...(overallShown ? (['overallTarget'] as const) : []),
    'description',
  ];
  const clear = fieldsToClear(stored, patch, edited);
  return clear.length ? { ...patch, clearFields: clear } : patch;
}

function buildFields(
  eventId: string,
  d: Draft,
  formLocked: boolean,
  stored: Event | undefined,
): UpdateEventInput {
  const storedStage = stored?.lifecycleStage;
  const nominationEnabled = d.nominationRequired === 'yes';
  const targetMode = effectiveTargetMode(d.targetMode, d.businessUnits);
  // Only BUs the event still has: a removed BU's row is gone, so its number is not sent.
  const byBu = targetsForSave(d.byBu, d.businessUnits)
    .map(([businessUnit, draft]) => ({ businessUnit, targets: targetsOf(draft) }))
    .filter((t) => t.targets !== undefined);

  return {
    eventId,
    eventName: d.eventName.trim() || undefined,
    eventType: (d.eventType || undefined) as EventType | undefined,
    eventSubType: (d.eventSubType || undefined) as EventSubType | undefined,
    province: d.province || undefined,
    city: d.city || undefined,
    sfdcCampaignId: d.sfdcCampaignId.trim() || undefined,
    startDate: fromDateInput(d.startDate),
    endDate: fromDateInput(d.endDate),
    // `[]` clears a stored list — an emptied BU picker must not leave the old BUs in place.
    businessUnits: listForSave(d.businessUnits, stored?.businessUnits),
    bdNominationSupported: nominationEnabled,
    nominationStartDate: nominationEnabled
      ? fromDateInput(d.nominationStartDate)
      : undefined,
    nominationEndDate: nominationEnabled ? fromDateInput(d.nominationEndDate) : undefined,
    // One mode or the other, never both: two sets of numbers that disagree about the same
    // event would leave nobody able to say which is the target.
    // A narrowed BU forces By BU — see buTargets.
    overallTarget: targetMode === 'overall' ? targetsOf(d.overall) : undefined,
    byBusinessUnitTargets: targetMode === 'byBu' ? byBu : undefined,
    /* The whole question list, every time — `nominationForm` is replace-not-merge, so this
       is also the only way to express "I deleted a question". That makes it the one field
       here where an EMPTY value is meaningful: `[]` clears the form, where every other
       empty box above sends undefined and leaves the stored value alone.

       Omitted entirely once the event is PUBLISHED. The service refuses the change then
       (answers are matched to questions by position), and sending the unchanged list would
       turn every other edit on this form into a 4xx. Also omitted while Nomination Required
       is No: the form is hidden then, so whatever it holds is left as stored. */
    // `formLocked` is "the event is PUBLISHED", which is also what lets the stage change.
    lifecycleStage: lifecycleStageToSend(formLocked, d.lifecycleStage, storedStage),
    // Images reduced to their ids; undefined when emptied, which buildPatch turns into a clear.
    description: descriptionForSave(d.description),
    nominationForm:
      formLocked || !nominationEnabled ? undefined : questionsForSave(d.nominationQuestions),
    /* The campaign hierarchy is not part of this patch: `UpdateEventInput` has no field for a
       tree. It is saved separately, as the service's campaign-node resources — see onSave and
       hierarchySync. `sfdcCampaignId` above is this event's OWN campaign id, unrelated to the
       hierarchy beneath it. */
  };
}

/** Re-exported so the edit page's tests can pin the saved question shape in one place. */
export { questionsForSave };

/** A single target cell. Kept narrow, since these are counts and not amounts. */
function TargetInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <FormField label={label}>
      <Input
        type="number"
        inputMode="numeric"
        value={value}
        placeholder="Placeholder"
        onChange={({ detail }) => onChange(detail.value)}
      />
    </FormField>
  );
}

export default function EditEventPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const canManage = canManageEvents(useEventHubRoles());

  const [draft, setDraft] = useState<Draft | null>(null);
  /**
   * The loaded event, kept for the creator check only.
   *
   * The service refuses UpdateEvent to anyone but the event's creator (`requireCreator`, no
   * admin exception), so the form is not offered to anyone else: filling it in and getting a 403
   * on Save is the worst way to learn that. The URL is reachable directly, so the detail page
   * hiding its Edit button is not enough on its own.
   */
  const [loadedEvent, setLoadedEvent] = useState<Event | null>(null);
  /**
   * Whether the loaded event is PUBLISHED, which locks the nomination form.
   *
   * Kept apart from the draft: it is the event's state, not a value a control edits, and it
   * must not change when the marketer edits the form. Publishing happens on the detail page,
   * so this is settled for as long as this page is open.
   */
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  useEventHubLoading(loading);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  /** The hierarchy as the service holds it — what a save diffs against (see hierarchySync). */
  const [loadedHierarchy, setLoadedHierarchy] = useState<LoadedHierarchy>(EMPTY_HIERARCHY);
  /**
   * Why the hierarchy could not be read, or null. While set the editor is locked: saving
   * without knowing what is already there would create the nodes a second time.
   */
  const [hierarchyError, setHierarchyError] = useState<string | null>(null);
  /**
   * What the hierarchy's event picker lists: every event, and which of them already back a
   * node. Read once, the first time the editor is shown — it is a full scan of the hierarchy
   * (see eventIdsWithNodes), so it is not paid by an edit that never opens the editor.
   */
  const [eventPicker, setEventPicker] = useState<EventPickerSource | undefined>(undefined);
  const wantPicker = !!draft && draft.configureHierarchy === 'yes' && !hierarchyError;

  const detailHref = `/event/events/${eventId}`;

  useEffect(() => {
    /* No request for someone the service would refuse anyway. The role gate below only
       decides what renders; without this the page would still have fired a GetEvent that
       comes back 403, which is a wasted round trip and a confusing log line. */
    if (!canManage) {
      setLoading(false);
      return;
    }
    if (!eventId) {
      setError('No event id in the URL.');
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    setLoading(true);
    setError(null);

    const client = eventHubCalendarClient();
    /* The hierarchy is read alongside the event. Its failure does not cost the page — the event
       can still be edited — but it locks the hierarchy editor (see hierarchyError). */
    const hierarchy = loadHierarchy(client.campaignHierarchy(), eventId).then(
      (h) => ({ ok: true as const, h }),
      (e: unknown) => ({ ok: false as const, message: e instanceof Error ? e.message : String(e) }),
    );
    Promise.all([client.getEvent(eventId, { signal: controller.signal }), hierarchy])
      .then(([event, loadedTree]) => {
        if (cancelled) return;
        const next = draftOf(event);
        if (loadedTree.ok) {
          setLoadedHierarchy(loadedTree.h);
          setHierarchyError(null);
          next.hierarchy = loadedTree.h.nodes;
          next.configureHierarchy = loadedTree.h.nodes.length ? 'yes' : 'no';
        } else {
          setHierarchyError(loadedTree.message);
        }
        setDraft(next);
        setPublished(event.contentStatus === 'PUBLISHED');
        setLoadedEvent(event);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (cancelled || controller.signal.aborted) return;
        setDraft(null);
        setError(e instanceof Error ? e.message : String(e));
        setLoading(false);
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [eventId, canManage]);

  useEffect(() => {
    if (!wantPicker || eventPicker) return;
    setEventPicker({ status: 'loading' });
    const client = eventHubCalendarClient();
    Promise.all([client.listEvents(), eventIdsWithNodes(client.campaignHierarchy())])
      .then(([events, withNodes]) => setEventPicker({ status: 'ready', events, withNodes }))
      .catch((e: unknown) =>
        setEventPicker({ status: 'error', message: e instanceof Error ? e.message : String(e) }),
      );
    // eventPicker is deliberately not a dependency: it is only ever set from here, once.
  }, [wantPicker]);

  /** Patches one field of the draft. */
  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
  }

  function setByBu(bu: string, patch: Partial<TargetDraft>) {
    setDraft((d) =>
      d
        ? {
            ...d,
            byBu: { ...d.byBu, [bu]: { ...(d.byBu[bu] ?? EMPTY_TARGET), ...patch } },
          }
        : d,
    );
  }

  /**
   * Saves, then returns to the event.
   *
   * One request. Publishing is a separate decision made on the detail page, so a failure
   * here has one cause and one message — the half-saved-half-published state this used to
   * be able to reach is gone.
   */
  async function onSave() {
    if (!eventId || !draft) return;
    setSaving(true);
    setSaveError(null);
    const client = eventHubCalendarClient();
    try {
      await client.updateEvent(buildPatch(eventId, draft, published, loadedEvent ?? undefined));
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : String(e));
      setSaving(false);
      return;
    }
    /* Then the hierarchy, as its own calls. Only when the editor is on and its state is known:
       'no' leaves whatever is saved alone, and a failed load must not be followed by a save
       that would duplicate every node. */
    if (draft.configureHierarchy === 'yes' && !hierarchyError) {
      const api = client.campaignHierarchy();
      try {
        await saveHierarchy(api, eventId, loadedHierarchy, draft.hierarchy);
      } catch (e: unknown) {
        /* The event itself is saved, and so is every hierarchy call before the one that
           failed. Re-read, so what is on screen — and what the next save diffs against — is
           what the service now holds. */
        const message = e instanceof Error ? e.message : String(e);
        try {
          const now = await loadHierarchy(api, eventId);
          setLoadedHierarchy(now);
          setDraft((d) => (d ? { ...d, hierarchy: now.nodes } : d));
        } catch {
          setHierarchyError('Could not re-read the hierarchy after a failed save.');
        }
        setSaveError(`The event was saved, but the hierarchy was not fully saved. ${message}`);
        setSaving(false);
        return;
      }
    }
    // Back to the event, which is where the change is visible. Staying put would leave the
    // user to work out whether anything happened.
    navigate(detailHref);
  }

  const breadcrumbs = (
    <BreadcrumbGroup
      items={[
        { text: 'Event Hub', href: '/event' },
        { text: 'Edit Event', href: '' },
      ]}
      onFollow={(e) => {
        e.preventDefault();
        if (e.detail.href) navigate(e.detail.href);
      }}
    />
  );

  const header = (
    <>
      {breadcrumbs}
      <Header variant="h1">Fill in Event Details</Header>
    </>
  );

  // The service allows PATCH /events/{eventId} to marketers and admins only. Rendering
  // the form for anyone else would collect edits that cannot be saved.
  if (!canManage) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="You cannot edit events">
          Editing an event requires a marketing or admin role in Event Hub.
        </Alert>
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout header={header}>
        <Alert type="error" header="Failed to load this event">
          {error}
        </Alert>
      </ContentLayout>
    );
  }

  if (loading || !draft) {
    // The layout's overlay shows meanwhile.
    return <ContentLayout header={header}>{null}</ContentLayout>;
  }

  const nominationEnabled = draft.nominationRequired === 'yes';
  /* Basic Information's BU narrows Set Target: Overall only when the event is All, and By BU
     rows only for the event's BUs. Derived, not stored, so widening the BU back to All restores
     the marketer's own Overall / By BU choice. */
  const allBus = isAllBusinessUnits(draft.businessUnits);
  const targetMode = effectiveTargetMode(draft.targetMode, draft.businessUnits);

  /* The stage picker sits in the page header, beside the title, as the Figma draws it. A DRAFT
     event is always PLANNING and the service refuses to change that, so the picker shows the
     stage but is disabled until the event is published. */
  const formHeader = (
    <>
      {breadcrumbs}
      <Header
        variant="h1"
        description={
          published ? undefined : 'The event stage can be changed once the event is published.'
        }
        actions={
          <RadioGroup
            direction="horizontal"
            ariaLabel="Event stage"
            value={draft.lifecycleStage ?? null}
            onChange={({ detail }) =>
              set('lifecycleStage', detail.value as EventLifecycleStage)
            }
            items={LIFECYCLE_STAGES.map(({ stage, label }) => ({
              value: stage,
              label,
              disabled: !published,
            }))}
          />
        }
      >
        Fill in Event Details
      </Header>
    </>
  );

  return (
    <ContentLayout header={formHeader}>
      <form onSubmit={(e) => e.preventDefault()}>
        <Form
          errorText={saveError ?? undefined}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => navigate(detailHref)}>
                Cancel
              </Button>
              {/* One save button. Save-as-Draft would be the same request — the service owns
                  contentStatus and does not accept it here — and publishing lives on the
                  detail page. */}
              <Button variant="primary" loading={saving} onClick={onSave}>
                Confirm
              </Button>
            </SpaceBetween>
          }
        >
          <SpaceBetween size="l">
            <FormField label="Event Name / Parent Event Name">
              <Input
                value={draft.eventName}
                onChange={({ detail }) => set('eventName', detail.value)}
              />
            </FormField>

            <Container header={<Header variant="h2">Basic Information</Header>}>
              <SpaceBetween size="l">
                <ColumnLayout columns={2}>
                  <FormField label="Event Type">
                    <Select
                      selectedOption={
                        TYPE_OPTIONS.find((o) => o.value === draft.eventType) ?? null
                      }
                      placeholder="Choose an option"
                      options={TYPE_OPTIONS}
                      onChange={({ detail }) =>
                        set('eventType', String(detail.selectedOption.value))
                      }
                    />
                  </FormField>
                  {/* Orthogonal to the type (any type can be any sub type), and what the
                      calendar's Event Sub Type filter and the table's column read. Optional. */}
                  <FormField label="Event Sub Type">
                    <Select
                      selectedOption={
                        SUB_TYPE_OPTIONS.find((o) => o.value === draft.eventSubType) ?? null
                      }
                      placeholder="Choose an option"
                      options={SUB_TYPE_OPTIONS}
                      onChange={({ detail }) =>
                        set('eventSubType', String(detail.selectedOption.value))
                      }
                    />
                  </FormField>
                  {/* Suggestions from the EC city list, stored as the event's two separate fields.
                      Either can also be typed, for a place the list lacks (an event abroad), and a
                      stored value the list lacks is shown as it is, not blanked — see cnRegions. */}
                  <FormField label="Province">
                    <Autosuggest
                      value={draft.province}
                      placeholder="Choose or type a province / region"
                      options={PROVINCE_OPTIONS}
                      enteredTextLabel={(v) => `Use "${v}"`}
                      ariaLabel="Province"
                      /* The city is checked against the province once it is decided — a suggestion
                         picked, or the box left — not on every keystroke: half-typed, "广" would
                         clear a city that "广东" keeps. */
                      onChange={({ detail }) => set('province', detail.value)}
                      onSelect={({ detail }) => {
                        const province = detail.value;
                        setDraft((d) =>
                          d && { ...d, province, city: cityAfterProvinceChange(province, d.city) },
                        );
                      }}
                      onBlur={() =>
                        setDraft((d) => d && { ...d, city: cityAfterProvinceChange(d.province, d.city) })
                      }
                    />
                  </FormField>
                  <FormField label="City">
                    <Autosuggest
                      value={draft.city}
                      placeholder="Choose or type a city, e.g. Las Vegas"
                      options={citiesOf(draft.province).map((c) => ({ value: c }))}
                      enteredTextLabel={(v) => `Use "${v}"`}
                      ariaLabel="City"
                      onChange={({ detail }) => set('city', detail.value)}
                    />
                  </FormField>

                  {/* Start and End share a row: they are read as one range. */}
                  <FormField label="Start Time">
                    <DatePicker
                      value={draft.startDate}
                      placeholder="YYYY/MM/DD"
                      onChange={({ detail }) => set('startDate', detail.value)}
                    />
                  </FormField>
                  <FormField label="End Time">
                    <DatePicker
                      value={draft.endDate}
                      placeholder="YYYY/MM/DD"
                      onChange={({ detail }) => set('endDate', detail.value)}
                    />
                  </FormField>

                  {/* "All" is the Multiselect's select-all row, so it stores every BU rather than
                      a sentinel the calendar's BU filter and the per-BU targets would not know. */}
                  <FormField label="BU">
                    <Multiselect
                      selectedOptions={BU_OPTIONS.filter((o) =>
                        draft.businessUnits.includes(String(o.value)),
                      )}
                      placeholder="Choose options"
                      options={BU_OPTIONS}
                      enableSelectAll
                      i18nStrings={{ selectAllText: 'All' }}
                      onChange={({ detail }) =>
                        set(
                          'businessUnits',
                          detail.selectedOptions.map((o) => String(o.value)),
                        )
                      }
                    />
                  </FormField>

                  {/* Not in the Figma — an omission, confirmed with the design owner:
                      UpdateEvent accepts this field and nothing else could set it. Placed
                      immediately before the hierarchy because the two read together: this
                      event's own campaign id, then the campaigns beneath it. */}
                  <FormField label="SFDC Campaign ID">
                    <Input
                      value={draft.sfdcCampaignId}
                      placeholder="SFDC Campaign ID"
                      onChange={({ detail }) => set('sfdcCampaignId', detail.value)}
                    />
                  </FormField>
                </ColumnLayout>

                <FormField label="Configure Event Hierarchy">
                    <RadioGroup
                      direction="horizontal"
                      value={draft.configureHierarchy}
                      onChange={({ detail }) =>
                        set('configureHierarchy', detail.value as 'no' | 'yes')
                      }
                      items={[
                        { value: 'no', label: 'No' },
                        { value: 'yes', label: 'Yes' },
                      ]}
                    />
                </FormField>

                {/* The design puts two fixed fields here, Child and Grandchild Campaign ID.
                    The requirement is a whole tree, so this is a tree editor instead. */}
                {hierarchyError ? (
                  <Alert type="warning" header="The event hierarchy could not be loaded">
                    {hierarchyError} — it cannot be edited until the page is reloaded.
                  </Alert>
                ) : draft.configureHierarchy === 'yes' ? (
                  <CampaignHierarchy
                    eventName={draft.eventName}
                    campaignId={draft.sfdcCampaignId}
                    value={draft.hierarchy}
                    onChange={(next) => set('hierarchy', next)}
                    eventId={eventId}
                    eventPicker={eventPicker}
                  />
                ) : null}

              </SpaceBetween>
            </Container>

            <Container header={<Header variant="h2">Event Introduction</Header>}>
              <RichTextEditor
                value={draft.description}
                ariaLabel="Event introduction"
                onChange={(html) => set('description', html)}
                onUploadImage={(file) =>
                  eventHubCalendarClient().uploadEventFile(eventId!, file)
                }
              />
            </Container>

            {/* Everything about nominations in one place, per testing feedback: the switch, its
                window and the form. The window and form show only once nominations are on —
                which is also when the service starts requiring both bounds to publish. */}
            <Container header={<Header variant="h2">Nomination</Header>}>
              <SpaceBetween size="l">
                <FormField label="Nomination Required">
                  <RadioGroup
                    direction="horizontal"
                    value={draft.nominationRequired}
                    onChange={({ detail }) =>
                      set('nominationRequired', detail.value as 'no' | 'yes')
                    }
                    items={[
                      { value: 'no', label: 'No' },
                      { value: 'yes', label: 'Yes' },
                    ]}
                  />
                </FormField>
                {nominationEnabled ? (
                  <>
                    <ColumnLayout columns={2}>
                      <FormField label="Nomination Registration Opens">
                        <DatePicker
                          value={draft.nominationStartDate}
                          placeholder="YYYY/MM/DD"
                          onChange={({ detail }) =>
                            set('nominationStartDate', detail.value)
                          }
                        />
                      </FormField>
                      <FormField label="Nomination Deadline">
                        <DatePicker
                          value={draft.nominationEndDate}
                          placeholder="YYYY/MM/DD"
                          onChange={({ detail }) => set('nominationEndDate', detail.value)}
                        />
                      </FormField>
                    </ColumnLayout>
                    <SpaceBetween size="s">
                      <Header variant="h3">Configure Nomination Form</Header>
                      <NominationFormConfig
                        value={draft.nominationQuestions}
                        onChange={(next) => set('nominationQuestions', next)}
                        locked={published}
                      />
                    </SpaceBetween>
                  </>
                ) : null}
              </SpaceBetween>
            </Container>

            <Container
              header={
                <Header
                  variant="h2"
                  description={
                    allBus
                      ? undefined
                      : 'Targets are set per BU because the event is limited to specific BUs.'
                  }
                  // The Overall / By BU choice sits in the header, as the Figma draws it.
                  actions={
                    <RadioGroup
                      direction="horizontal"
                      value={targetMode}
                      onChange={({ detail }) =>
                        set('targetMode', detail.value as TargetMode)
                      }
                      items={[
                        { value: 'overall', label: 'Overall', disabled: !allBus },
                        { value: 'byBu', label: 'By BU' },
                      ]}
                    />
                  }
                >
                  Set Target
                </Header>
              }
            >
              {targetMode === 'overall' ? (
                <ColumnLayout columns={2}>
                  <TargetInput
                    label="Nomination Target"
                    value={draft.overall.nomination}
                    onChange={(v) => set('overall', { ...draft.overall, nomination: v })}
                  />
                  <TargetInput
                    label="Registration Target"
                    value={draft.overall.registration}
                    onChange={(v) =>
                      set('overall', { ...draft.overall, registration: v })
                    }
                  />
                  <TargetInput
                    label="Attendance Target"
                    value={draft.overall.attendance}
                    onChange={(v) => set('overall', { ...draft.overall, attendance: v })}
                  />
                </ColumnLayout>
              ) : (
                /* A row per BU the event has (every BU when it is All — see buTargets), from
                   the one canonical BU list. The Figma's mock table
                   lists eight slightly different names (FSI vs FSI-DNB, plus C2G and
                   Others); following it would put a second spelling of the same BU into
                   the data, which businessUnits.ts exists to prevent. */
                <table className="eh-bu-targets">
                  <thead>
                    <tr>
                      <th scope="col">BU Name</th>
                      <th scope="col">Nomination Target</th>
                      <th scope="col">Registration Target</th>
                      <th scope="col">Attendance Target</th>
                    </tr>
                  </thead>
                  <tbody>
                    {targetBusinessUnits(draft.businessUnits).map((bu) => {
                      const row = draft.byBu[bu] ?? EMPTY_TARGET;
                      return (
                        <tr key={bu}>
                          <th scope="row">{bu}</th>
                          {(
                            [
                              ['nomination', 'Nomination'],
                              ['registration', 'Registration'],
                              ['attendance', 'Attendance'],
                            ] as const
                          ).map(([field, name]) => (
                            <td key={field}>
                              <Input
                                type="number"
                                inputMode="numeric"
                                value={row[field]}
                                placeholder="Placeholder"
                                ariaLabel={`${name} target for ${bu}`}
                                onChange={({ detail }) =>
                                  setByBu(bu, { [field]: detail.value })
                                }
                              />
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </Container>

          </SpaceBetween>
        </Form>
      </form>
    </ContentLayout>
  );
}
